import { useEffect } from 'react';
import type { FC } from 'react';
import { NavBarWrapper, Brand, NavLoanInfoText, Col, SupportPhone, NavBarLogo } from './NavBar.styles';
import { useAppSelector } from '@app/hooks';
import { useIsMobile } from '@/hooks/useIsMobile';
import { handleCurrentVmoPage } from '@/utils/handleCurrentPage';
import { routeToPageIndex } from '@/helper/pageOrder';
import { ConnectSupport } from './NavBar.styles';
import { useLocation } from 'react-router-dom';

export interface NavBarProps {
  variant: 'primary' | 'secondary' | 'default';
  storybookLabel?: {
    pageLabel?: string;
    helpLabel?: string;
    supportLabel?: string;
    loanLabel?: string;
  };
  color?: string;
  backgroundColor?: string;
  isCustomColor?: boolean;
  isGetStarted?: boolean;
  isStartedPage?: boolean;
}

export const shouldRedirectForMissingApplicationContext = ({
  configurationId,
  isGetStarted,
  isLpGateway,
  lpApplicationId,
  lpToken,
}: {
  configurationId?: string;
  isGetStarted: boolean;
  isLpGateway?: boolean;
  lpApplicationId?: string;
  lpToken?: string;
}): boolean => !configurationId && !(isLpGateway && lpApplicationId && lpToken) && !isGetStarted;

export const NavBar: FC<NavBarProps> = ({ variant = 'primary', storybookLabel, color, isCustomColor = false, backgroundColor = '', isStartedPage = true }) => {
  const { step, value } = useAppSelector((state) => state.currentPage);
  const isMobile = useIsMobile();
  const location = useLocation();
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const lpApplication = useAppSelector((state) => state.LpApplication);
  const hasDrawPage = templateConfig?.pages?.hasDrawAmountPage ?? false;
  const { isEnabled: isVmoRedesignEnabled = false } = templateConfig?.features?.vmoRedesign ?? {};
  const hasDeclarations = !!templateConfig?.pages?.declarations;
  const isGlanceEnabled = templateConfig?.glanceSettings?.isEnabled;
  const pagesWithVmoVersion = handleCurrentVmoPage(isVmoRedesignEnabled, hasDrawPage, hasDeclarations, templateConfig);
  const isGetStarted = templateConfig?.pages?.pages?.[step] === templateConfig?.pages?.pages?.[0] && step === 0 && isStartedPage;
  const configuredLoanNumberLabel = templateConfig?.pages?.navBar?.loanNumber?.text?.trim() || 'Application Number:';
  let isVmoPage;

  // page name mapping as in SideBar
  const pageRouteMap: { [key: string]: string } = {
    '/get-started': "Let's Get Started",
    '/get-started-address': "Let's Get Started",
    '/estimated-available-equity': 'Estimated Available Equity',
    '/vmo': 'View My Offer!',
    '/vmo-line-amount': 'View My Offer!',
    '/vmo-draw-amount': 'View My Offer!',
    '/personal-information': 'Personal Information',
    '/employment-information': 'Employment Details',
    '/government-monitoring-information': 'Demographic Information',
    '/declarations': 'Declarations',
    '/inquiry-summary': 'Summary',
    '/completion': 'Completion',
  };

  const getConfigPageIndex = (route: string): number | undefined => {
    const configIndex = routeToPageIndex[route];
    if (configIndex === undefined) return undefined;

    const declarationsConfigIndex = routeToPageIndex['/declarations'];
    if (!hasDeclarations && configIndex > declarationsConfigIndex) {
      return configIndex - 1;
    }

    return configIndex;
  };

  const getPageName = () => {
    if (storybookLabel?.pageLabel) {
      return storybookLabel.pageLabel;
    }

    // get page name from current URL, preferring lender-configured title from templateConfig
    const currentUrlPage = location.pathname;
    const pageIndex = getConfigPageIndex(currentUrlPage);

    if (pageIndex !== undefined) {
      const configuredName = templateConfig?.pages?.pages?.[pageIndex];
      const pageName = configuredName || pageRouteMap[currentUrlPage];
      isVmoPage = !!pageName?.startsWith('View My Offer');
      return pageName;
    }

    // original step-based logic fallback
    let pageName = pagesWithVmoVersion[step];
    // Normalize old page names to new ones
    if (pageName === 'Government Info' || pageName === 'Government Monitoring') {
      pageName = 'Demographic Information';
    }
    isVmoPage = pageName === 'View My Offer! (Available Programs)';
    return isMobile && isVmoPage ? 'View My Offer!' : pageName;
  };
  const isLoanDepot = templateConfig?.lenderName?.toLowerCase() === 'loandepot';
  const isCompletionPage = value === 'Completion';
  const isPhoneHelp = !isCompletionPage && !isMobile && templateConfig?.pages?.navBar?.phoneHelp;
  const showConnectSupport = isGlanceEnabled || !templateConfig?.pages?.navBar?.connectSupport?.isHidden;
  const connectSupportText = storybookLabel?.loanLabel || templateConfig?.pages?.navBar?.connectSupport?.text || '';
  const phoneHelpText = `${storybookLabel?.loanLabel || templateConfig?.pages?.navBar?.phoneHelp?.text || ''} ${templateConfig?.phoneContact || ''}`;
  const logo = templateConfig?.fixedValues?.logoInCongratsScreen || templateConfig?.globalTheme?.logo;
  const componentNavBarColor = color ?? templateConfig?.globalTheme?.navbarFontColor ?? '#ffffff';
  const shouldRedirect = shouldRedirectForMissingApplicationContext({
    configurationId: loanApplication.configurationId,
    isGetStarted,
    isLpGateway: templateConfig?.isLpGateway,
    lpApplicationId: lpApplication?.applicationId,
    lpToken: lpApplication?.token,
  });

  useEffect(() => {
    if (shouldRedirect) {
      window.location.href = '/';
    }
  }, [shouldRedirect]);

  return (
    <NavBarWrapper $variant={variant} data-testid="nav-bar" $backgroundColor={backgroundColor} $isCustomColor={isCustomColor} $isGetStarted={isGetStarted}>
      {isCompletionPage && isLoanDepot ? (
        <NavBarLogo>
          <img src={logo} alt={`${templateConfig?.lenderName}-logo`} />
        </NavBarLogo>
      ) : (
        <Brand $variant={variant} data-testid="brand-bar" $color={componentNavBarColor} $isCustomColor={isCustomColor}>
          {getPageName()}
        </Brand>
      )}
      {isPhoneHelp && (
        <SupportPhone>
          <NavLoanInfoText $isGetStarted={isGetStarted} $isHidden={templateConfig?.pages?.navBar?.phoneHelp?.isHidden ?? false}>
            <Col $variant={variant} $color={componentNavBarColor} $isCustomColor={isCustomColor}>
              {showConnectSupport && (
                <ConnectSupport
                  $isHidden={!isGlanceEnabled}
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as any).GLANCE?.Cobrowse?.Visitor) {
                      (window as any).GLANCE.Cobrowse.Visitor.startSession();
                    }
                  }}
                >
                  {connectSupportText ?? ''}
                </ConnectSupport>
              )}
              <p>{phoneHelpText ?? ''}</p>
            </Col>
          </NavLoanInfoText>
        </SupportPhone>
      )}
      {!isGetStarted && (
        <NavLoanInfoText $isGetStarted={isGetStarted} $isHidden={templateConfig?.pages?.navBar?.loanNumber?.isHidden ?? false}>
          <Col $variant={variant} $color={componentNavBarColor} $isCustomColor={isCustomColor}>
            {templateConfig?.isLpGateway
              ? `${configuredLoanNumberLabel} ${loanApplication?.loanNumber}`
              : storybookLabel?.loanLabel || `${templateConfig?.pages?.navBar?.loanNumber?.text} ${loanApplication?.loanNumber}` || 'Loan Number: '}
          </Col>
        </NavLoanInfoText>
      )}
    </NavBarWrapper>
  );
};
